"use client";

import { useState, useRef, useEffect } from "react";
import { Link as NextIntlLink } from "@/i18n/navigation";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ReserveButton } from "@/components/buttons/ReserveButton";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
interface HeaderProps {
  className?: string;
}

export const Header = ({ className = "" }: HeaderProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPassedHero, setIsPassedHero] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const [mounted, setMounted] = useState(false);

  const navLinks = [
    {
      name: t("navigation.vehicles"),
      href: `/${locale}/#${t("sections.vehicles")}`,
    },
    { name: t("navigation.about"), href: `/${locale}/#${t("sections.about")}` },
    {
      name: t("navigation.howItWorks"),
      href: `/${locale}/#${t("sections.how")}`,
    },
    { name: t("navigation.whyUs"), href: `/${locale}/#${t("sections.why")}` },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  };

  const handleMenuClick = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = "";
  };

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY < lastScrollY.current || currentScrollY < 50);
      setIsPassedHero(currentScrollY > 50);
      lastScrollY.current = currentScrollY;
    };

    handleScroll(); // Initial check
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.style.overflow = "";
    };
  }, []);

  if (!mounted) {
    return null; // ou un placeholder/skeleton
  }

  const menuVariants = {
    closed: {
      opacity: 0,
      x: "100%",
      transition: {
        duration: 0.5,
        ease: [0.32, 0.72, 0, 1],
      },
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: [0.32, 0.72, 0, 1],
      },
    },
  };

  const menuItemVariants = {
    closed: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.3,
      },
    },
    open: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: i * 0.1,
      },
    }),
  };

  return (
    <>
      <motion.header
        ref={headerRef}
        initial={{ y: 0 }}
        animate={{ y: isVisible ? 0 : -100 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className={`fixed left-0 right-0 z-40 px-4 py-4 ph:px-24 md:px-48 lg:px-72 2xl:px-96 ${className} ${
          isPassedHero && !isMenuOpen
            ? "top-0 h-32 bg-backgroundSecondary text-white"
            : "!h-fit bg-transparent py-8 text-white"
        }`}
      >
        <div className="flex h-full items-center justify-between">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <NextIntlLink href="/" onClick={() => isMenuOpen && toggleMenu()}>
              {/* <h1 className="text-3xl font-extralight">{t("logo")}</h1> */}
              <Image
                src="/images/logo-ce.png"
                alt="logo"
                width={129}
                height={50}
              />
            </NextIntlLink>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-4 xl:flex">
            <LanguageSwitcher />
            {navLinks.map((item) => (
              <motion.div
                key={item.name}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  href={item.href}
                  className="rounded-full border border-white px-5 py-1 text-sm font-light text-white transition-all duration-300 hover:bg-white/10"
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
            <ReserveButton />
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="xl:hidden"
            onClick={toggleMenu}
            title={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            <Icon
              icon={isMenuOpen ? "mdi:close" : "mdi:menu"}
              className="h-10 w-10 text-white"
            />
          </motion.button>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="fixed inset-0 z-50 bg-backgroundSecondary/95 backdrop-blur-sm xl:hidden"
          >
            <div className="container mx-auto flex h-full flex-col items-center justify-center space-y-8 px-4 py-8">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                title="Fermer le menu"
                onClick={toggleMenu}
                className="absolute right-4 top-4 text-white"
                variants={menuItemVariants}
                custom={0}
              >
                <Icon icon="mdi:close" className="h-10 w-10" />
              </motion.button>
              {[
                { href: "/", text: t("navigation.home") },
                {
                  href: `/${locale}/#${t("sections.vehicles")}`,
                  text: t("navigation.vehicles"),
                },
                {
                  href: `/${locale}/#${t("sections.about")}`,
                  text: t("navigation.about"),
                },
                {
                  href: `/${locale}/#${t("sections.how")}`,
                  text: t("navigation.howItWorks"),
                },
                {
                  href: `/${locale}/#${t("sections.why")}`,
                  text: t("navigation.whyUs"),
                },
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  variants={menuItemVariants}
                  custom={index + 1}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    href={item.href}
                    onClick={handleMenuClick}
                    className="text-2xl font-light text-white"
                  >
                    {item.text}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                className="mt-8 flex flex-col items-center gap-6"
                variants={menuItemVariants}
                custom={6}
              >
                <div onClick={handleMenuClick}>
                  <ReserveButton />
                </div>
                <LanguageSwitcher />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
